import { Headline } from "@App/Components/atoms/common/Headline";
import { Button } from "@App/Components/Funnel/Button";
import { useReturnSubmit } from "@App/hooks/vehicleReturn/useReturnSubmit";
import type { State } from "@App/state";
import {
  selectContractDetails,
  selectDesiredReturnDates,
  selectIsAuthenticated,
  selectPickUpAddress,
} from "@App/state/selectors/vehicleReturn";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { type FunctionComponent, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

export type DeliverySubmitProps = {
  onSubmit: () => void;
};
export const ReturnSubmit: FunctionComponent<DeliverySubmitProps> = ({
  onSubmit,
}: DeliverySubmitProps) => {
  const { t } = useTranslation();

  const isAuthenticated = useSelector(selectIsAuthenticated);

  const navigate = useNavigate();
  useEffect(() => {
    if (!isAuthenticated) navigate("../", { replace: true });
  }, [isAuthenticated, navigate]);

  const organization = useSelector((state: State) => state.orders.organization);
  const contractDetails = useSelector(selectContractDetails);
  const pickupAddress = useSelector(selectPickUpAddress);
  const desiredReturnDates = useSelector(selectDesiredReturnDates);

  const { error, loading, handleSubmit } = useReturnSubmit(
    organization.id,
    contractDetails,
    pickupAddress,
    Object.values(desiredReturnDates),
    onSubmit,
  );

  return (
    <div
      className={
        "container md:w-[600px] m-5 h-[calc(100vh-150px)] flex items-start md:items-center mx-auto"
      }
    >
      <div>
        <Headline type="h1">{t("vehicleReturn.submit-headline")}</Headline>
        <div className="font-bold">{t("vehicleReturn.submit-identity-headline")}</div>
        <div className="text-neutral-500 mb-3">{contractDetails.agreementNumber}</div>

        <div className="flex flex-col md:flex-row mb-3">
          <div className="flex-1">
            <div className="font-bold">{t("vehicleReturn.submit-address-headline")}</div>
            <div className="text-neutral-500 mb-3">
              {pickupAddress.name}
              <br />
              {pickupAddress.phone}
              <br />
              {pickupAddress.street}
              <br />
              {pickupAddress.postcode} {pickupAddress.city}
            </div>
            <div className="font-bold">{t("vehicleReturn.submit-email-headline")}</div>
            <div className="text-neutral-500 mb-3">{pickupAddress.email}</div>
          </div>
          <div className="flex-1">
            <div className="font-bold">{t("vehicleReturn.submit-date-headline")}</div>
            <div className="text-neutral-500 mb-3">
              1.{" "}
              {desiredReturnDates.date1 &&
                format(desiredReturnDates.date1, "iiii - dd.MM.yyyy", {
                  locale: de,
                })}
              <br />
              2.{" "}
              {desiredReturnDates.date2 &&
                format(desiredReturnDates.date2, "iiii - dd.MM.yyyy", {
                  locale: de,
                })}
              <br />
              3.{" "}
              {desiredReturnDates.date3 &&
                format(desiredReturnDates.date3, "iiii - dd.MM.yyyy", {
                  locale: de,
                })}
            </div>
          </div>
        </div>

        <p className={"text-neutral-500 mb-3"}>
          <Trans i18nKey="vehicleReturn.submit-text" />
        </p>

        {error && (
          <p className={"text-error-500"}>
            Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.
          </p>
        )}

        <Button
          disabled={loading}
          title={loading ? "Buchen..." : t("vehicleReturn.submit-button")}
          className={"md:!w-1/2"}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};
